/**
 * Orchestration Status View
 *
 * Renders the smart-orchestration journey of a transaction as a phase timeline
 * (Health Check → Account Aggregator → PDF → Smart Routing Decision), matching
 * the Consent Logs / Data Logs tabs. Data comes from the GMS BFF via the
 * FinProAdminServer proxy; this component is presentational only — the parent
 * fetches and passes data/loading/error.
 *
 * Status text is rendered verbatim from the API response (the backend may
 * change casing/wording over time); we only derive a colour from it for the
 * dot/badge tint, defaulting to neutral grey when unrecognised.
 */

import React, { useMemo, useState } from 'react';
import { Loader } from 'equal-ds-ui';
import {
  AlertCircle,
  CheckCircle2,
  ChevronRight,
  Clock,
  GitBranch,
  MinusCircle,
  XCircle,
} from 'lucide-react';
import type {
  OrchestrationPhase,
  OrchestrationStatusData,
} from '../../../types/timeline';

interface OrchestrationStatusViewProps {
  data: OrchestrationStatusData | null;
  isLoading?: boolean;
  error?: string | null;
}

// Friendly label + subtitle per phase type (unknown types fall back to humanize()).
const PHASE_META: Record<string, { label: string; subtitle: string }> = {
  health_check: { label: 'Health Check', subtitle: 'FIP reachability before routing' },
  aa: { label: 'Account Aggregator', subtitle: 'Consent & linked accounts via AA' },
  pdf: { label: 'PDF Statements', subtitle: 'Uploaded · processed · fetched' },
  smart_routing_decision: { label: 'Smart Routing Decision', subtitle: 'Chosen data source for this transaction' },
};

type StatusKind = 'success' | 'running' | 'failed' | 'skipped' | 'neutral';

// Colour + icon only — the displayed text is always the raw API status string.
const STATUS_STYLE: Record<
  StatusKind,
  { badge: string; dot: string; icon: React.ComponentType<{ className?: string }> }
> = {
  success: { badge: 'bg-green-100 text-green-700 border-green-200', dot: 'bg-green-100 text-green-600 border-green-200', icon: CheckCircle2 },
  running: { badge: 'bg-blue-100 text-blue-700 border-blue-200', dot: 'bg-blue-100 text-blue-600 border-blue-200', icon: Clock },
  failed: { badge: 'bg-red-100 text-red-700 border-red-200', dot: 'bg-red-100 text-red-600 border-red-200', icon: XCircle },
  skipped: { badge: 'bg-gray-100 text-gray-600 border-gray-200', dot: 'bg-gray-100 text-gray-500 border-gray-200', icon: MinusCircle },
  neutral: { badge: 'bg-gray-100 text-gray-600 border-gray-200', dot: 'bg-gray-100 text-gray-500 border-gray-200', icon: MinusCircle },
};

/** Best-effort colour bucket for a raw status string (used for tint only). */
function toStatusKind(raw?: string): StatusKind {
  const s = (raw ?? '').toLowerCase();
  // Failure first — so "unsuccessful" (which contains "success") is caught here, not below.
  if (/(unsuccess|fail|error|reject|declin|deni|cancel|timeout|timed[_ ]?out)/.test(s)) return 'failed';
  if (/(success|complete|done|approved|ok|routed|fetched)/.test(s)) return 'success';
  if (/(progress|running|pending|processing|initiated|started)/.test(s)) return 'running';
  if (/(skip|not[_ ]?required|^na$|^n\/a$)/.test(s)) return 'skipped';
  return 'neutral';
}

// Full-key label overrides for keys that don't humanize cleanly.
const LABEL_OVERRIDES: Record<string, string> = {
  decryptecreserrorcode: 'Error Code',
};
// Humanize a snake/kebab key into a label, upper-casing known acronyms.
const ACRONYMS: Record<string, string> = {
  fip: 'FIP', fips: 'FIPs', aa: 'AA', pdf: 'PDF', id: 'ID',
  txn: 'Txn', url: 'URL', vua: 'VUA', tat: 'TAT',
};
function humanize(key: string): string {
  const override = LABEL_OVERRIDES[key.toLowerCase()];
  if (override) return override;
  return key
    .split(/[_-]+/)
    .filter(Boolean)
    .map((w) => ACRONYMS[w.toLowerCase()] ?? w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');
}

function metaFor(type: string) {
  return PHASE_META[type] ?? { label: humanize(type), subtitle: '' };
}

// Renders a phase-detail value: chips for string lists, a table for object lists,
// label/value rows for nested objects, "—"/"None" for empty.
const ValueView: React.FC<{ value: unknown }> = ({ value }) => {
  if (value === null || value === undefined || value === '') {
    return <span className="text-text-tertiary">—</span>;
  }
  if (Array.isArray(value)) {
    if (value.length === 0) return <span className="text-text-tertiary">None</span>;
    const allPrimitive = value.every((v) => v === null || typeof v !== 'object');
    if (allPrimitive) {
      return (
        <div className="flex flex-wrap gap-1.5">
          {value.map((v, i) => (
            <span key={i} className="px-2 py-0.5 rounded-md bg-gray-100 text-text-secondary text-[11.5px] font-medium">
              {String(v)}
            </span>
          ))}
        </div>
      );
    }
    const cols = Array.from(
      value.reduce<Set<string>>((set, o) => {
        if (o && typeof o === 'object') Object.keys(o as object).forEach((k) => set.add(k));
        return set;
      }, new Set<string>()),
    );
    return (
      <div className="overflow-x-auto">
        <table className="w-full border-collapse text-[12px]">
          <thead>
            <tr>
              {cols.map((c) => (
                <th key={c} className="text-left font-semibold text-text-secondary px-2 py-1 border-b border-border-default text-[11px]">
                  {humanize(c)}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {value.map((o, i) => (
              <tr key={i}>
                {cols.map((c) => (
                  <td key={c} className="px-2 py-1.5 border-b border-gray-100 align-top">
                    <ValueView value={(o as Record<string, unknown>)?.[c]} />
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }
  if (typeof value === 'object') {
    return (
      <dl className="grid grid-cols-[130px_minmax(0,1fr)] gap-x-3 gap-y-1">
        {Object.entries(value as Record<string, unknown>).map(([k, v]) => (
          <React.Fragment key={k}>
            <dt className="text-text-tertiary">{humanize(k)}</dt>
            <dd className="m-0 min-w-0"><ValueView value={v} /></dd>
          </React.Fragment>
        ))}
      </dl>
    );
  }
  return <span className="text-text-primary break-words">{String(value)}</span>;
};

/** Render a phase's `data` blob as readable label/value rows. */
const PhaseDetail: React.FC<{ data: unknown }> = ({ data }) => {
  if (data === null || data === undefined || (typeof data === 'object' && Object.keys(data as object).length === 0)) {
    return <p className="text-xs text-text-tertiary pt-2">No additional details for this phase.</p>;
  }
  if (typeof data !== 'object') {
    return <p className="text-sm text-text-primary pt-2">{String(data)}</p>;
  }
  return (
    <dl className="grid grid-cols-[150px_minmax(0,1fr)] gap-x-4 gap-y-2.5 pt-2 text-[12.5px] items-start">
      {Object.entries(data as Record<string, unknown>).map(([key, value]) => (
        <React.Fragment key={key}>
          <dt className="text-text-secondary font-medium">{humanize(key)}</dt>
          <dd className="m-0 min-w-0 text-text-primary break-words"><ValueView value={value} /></dd>
        </React.Fragment>
      ))}
    </dl>
  );
};

export const OrchestrationStatusView: React.FC<OrchestrationStatusViewProps> = ({
  data,
  isLoading,
  error,
}) => {
  // Which phases appear (and whether each has detail) is decided by the backend;
  // the view just renders what it receives.
  const phases: OrchestrationPhase[] = useMemo(() => data?.data ?? [], [data]);

  // First phase expanded by default. toggle uses the same `idx === 0` default as
  // isOpen so the first click inverts what's actually displayed (not a stale false).
  // Keyed by `${type}-${idx}` (same as the list key) so duplicate types stay independent.
  // toggle uses the same `idx === 0` default as isOpen so the first click inverts correctly.
  const [openTypes, setOpenTypes] = useState<Record<string, boolean>>({});
  const isOpen = (rowKey: string, idx: number) => openTypes[rowKey] ?? idx === 0;
  const toggle = (rowKey: string, idx: number) =>
    setOpenTypes((prev) => ({ ...prev, [rowKey]: !(prev[rowKey] ?? idx === 0) }));

  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center flex-1 py-12">
        <Loader size={64} cellSize={16} />
        <p className="text-sm font-medium text-text-secondary mt-5">Loading orchestration status...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center flex-1 text-center py-12 px-6">
        <AlertCircle className="w-12 h-12 text-red-500 mb-3" />
        <p className="text-sm font-medium text-text-primary">Couldn’t load orchestration status</p>
        <p className="text-xs text-text-tertiary mt-1 max-w-sm">{error}</p>
      </div>
    );
  }

  if (!data || phases.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center flex-1 text-center py-12 px-6">
        <GitBranch className="w-12 h-12 text-gray-300 mb-3" />
        <p className="text-sm font-medium text-text-primary">No orchestration data available</p>
        <p className="text-xs text-text-tertiary mt-1 max-w-sm">
          This transaction has no recorded smart-orchestration phases yet.
        </p>
      </div>
    );
  }

  const overallKind = toStatusKind(data.status);

  return (
    <div className="flex flex-col gap-3 py-2">
      <div className="flex flex-wrap items-center gap-x-6 gap-y-2 px-1 pb-1 text-xs text-text-secondary">
        {data.transaction_id && (
          <span>
            Workflow Txn Id{' '}
            <b className="font-mono text-[11.5px] text-text-primary">{data.transaction_id}</b>
          </span>
        )}
        {data.account_id && (
          <span>
            Account <b className="font-mono text-[11.5px] text-text-primary">{data.account_id}</b>
          </span>
        )}
        {data.status && (
          <span
            className={`inline-flex items-center px-2.5 py-0.5 rounded-full border text-[11px] font-semibold ${STATUS_STYLE[overallKind].badge}`}
          >
            {data.status}
          </span>
        )}
      </div>

      <div className="relative pl-8">
        <span className="absolute left-[11px] top-3 bottom-4 w-0.5 bg-gray-300" />
        {phases.map((phase, idx) => {
          const meta = metaFor((phase.type ?? '').toLowerCase());
          const kind = toStatusKind(phase.status);
          const sStyle = STATUS_STYLE[kind];
          const Icon = sStyle.icon;
          const rowKey = `${phase.type}-${idx}`;
          const hasDetail =
            !!phase.data && typeof phase.data === 'object' && Object.keys(phase.data).length > 0;
          const open = hasDetail && isOpen(rowKey, idx);

          const header = (
            <>
              <span>
                <span className="block text-[14px] font-semibold text-text-primary tracking-tight">
                  {meta.label}
                </span>
                {meta.subtitle && (
                  <span className="block text-[11.5px] text-text-tertiary mt-0.5">{meta.subtitle}</span>
                )}
              </span>
              {phase.status && (
                <span
                  className={`ml-auto px-2.5 py-1 rounded-full border text-[11px] font-semibold whitespace-nowrap ${sStyle.badge}`}
                >
                  {phase.status}
                </span>
              )}
              {hasDetail && (
                <ChevronRight
                  className={`w-4 h-4 text-text-tertiary flex-none transition-transform ${open ? 'rotate-90' : ''}`}
                />
              )}
            </>
          );

          return (
            <div key={rowKey} className="relative mb-3.5">
              <span
                className={`absolute -left-8 top-4 w-6 h-6 rounded-full grid place-items-center border-2 z-[1] ${sStyle.dot}`}
              >
                <Icon className="w-3.5 h-3.5" />
              </span>
              <div className="bg-white border border-border-default rounded-xl shadow-sm overflow-hidden">
                {hasDetail ? (
                  <button
                    type="button"
                    onClick={() => toggle(rowKey, idx)}
                    aria-expanded={open}
                    className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-blue-50/40 transition-colors"
                  >
                    {header}
                  </button>
                ) : (
                  <div className="w-full flex items-center gap-3 px-4 py-3">{header}</div>
                )}
                {open && (
                  <div className="border-t border-dashed border-border-default px-4 pt-1 pb-4">
                    <PhaseDetail data={phase.data} />
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};
